import { Module } from 'vuex';
import {
  ICarouselBanner,
  ICmsContentState,
  IInventoryCollection,
  ISimpleBanner,
  ISanityHomepageTakeOver,
} from './types';
import { IRootState } from '../types';
import sanityService, { ISanityGame } from '~/services/sanity.service';
import { IGame } from '../games/types';

const namespaced = true;

function sanitizeGames(games: ISanityGame[], i18n: any): IGame[] {
  return games.map(game => ({
    ...game,
    displayedPlatform: game.platform
      .map(platform => i18n.t(`store.games.index.platforms.${platform}`))
      .join('/'),
    craftedItems: [],
    logo: game.gamePageHeaderLogo,
    showNodePromotion: true,
    descriptionAssets: [
      {
        thumbnail: game.trailerThumbnail,
        medium: game.trailer,
        poster: game.trailerThumbnail,
        type: 'video' as 'video',
      },
      ...game.carouselImages.map(image => ({
        thumbnail: image,
        medium: image,
        large: image,
        type: 'img' as 'img',
      })),
    ].filter(a => a.thumbnail),
  }));
}

function sanitizeCarouselBanners(banners: ICarouselBanner[]) {
  return banners.map(b => ({
    ...b,
    learnMoreLink: b.learnMoreLink?.replace('https://app.gala.games', ''),
    playLink: b.playLink?.replace('https://app.gala.games', ''),
  }));
}

function sanitizeHPTOs(hptos: ISanityHomepageTakeOver) {
  return hptos;
}

const cmsContent: Module<ICmsContentState, IRootState> = {
  namespaced,
  state: {
    simpleBanners: [],
    carouselBanners: [],
    inventoryCollections: [],
    homepageTakeover: {
      name: '',
      enabled: false,
      logo: '',
      startDate: '',
      endDate: '',
      noVideoMobile: false,
      bgImageDesktop: '',
      bgImageMobile: '',
      bgVideo: '',
      ctaColor: '',
      ctaLink: '',
      ctaText: '',
      ctaTextColor: '',
      customFont: '',
      customStyles: '',
      glowColor: '',
      headline: '',
      subhead: '',
      textColor: '',
      textShadow: false,
    },
  },
  getters: {
    inventoryIconForCollectionName: (state: ICmsContentState) => {
      return Object.fromEntries(
        state.inventoryCollections.map(collection => [
          collection.name,
          collection.inventoryImage,
        ]),
      );
    },
    mainBanner: (state: ICmsContentState) => {
      return state.simpleBanners.find(banner => banner.showOnMain);
    },
    storeBanner: (state: ICmsContentState) => {
      return state.simpleBanners.find(banner => banner.showOnStore);
    },
  },
  actions: {
    loadCmsContent: async (
      context,
      args: {
        i18n: any;
        sentry: { captureException: (a: unknown) => unknown };
      },
    ) => {
      const locale = args.i18n.locale;

      const results = await Promise.allSettled([
        sanityService
          .getCarouselBanners(locale)
          .then(carouselBanners =>
            context.commit(
              'setCarouselBanners',
              sanitizeCarouselBanners(carouselBanners),
            ),
          ),
        sanityService
          .getSimpleBanners(locale)
          .then(simpleBanners =>
            context.commit('setSimpleBanners', simpleBanners),
          ),
        sanityService
          .getInventoryCollections()
          .then(inventoryCollections =>
            context.commit('setInventoryCollections', inventoryCollections),
          ),
        sanityService.getGames(locale).then(games =>
          context.commit('games/setGames', sanitizeGames(games, args.i18n), {
            root: true,
          }),
        ),
        sanityService.getHomepageTakeOvers(locale).then(hptos => {
          context.commit('setHomepageTakeover', sanitizeHPTOs(hptos));
        }),
      ]);

      for (const result of results) {
        if (result.status === 'rejected') {
          console.warn(result.reason);
          args.sentry.captureException(result.reason);
        }
      }
    },
  },
  mutations: {
    setCarouselBanners(state, banners: ICarouselBanner[]) {
      state.carouselBanners = [...banners];
    },
    setSimpleBanners(state, banners: ISimpleBanner[]) {
      state.simpleBanners = [...banners];
    },
    setInventoryCollections(state, collections: IInventoryCollection[]) {
      state.inventoryCollections = [...collections];
    },
    setHomepageTakeover(state, hpto: ISanityHomepageTakeOver) {
      state.homepageTakeover = hpto;
    },
  },
};

export default cmsContent;
